import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { SyncQueueItem } from '@/types'

interface OfflineState {
  isOnline: boolean
  isSyncing: boolean
  lastSyncAt: Date | null
  pendingChanges: number
  syncQueue: SyncQueueItem[]
  failedSyncs: SyncQueueItem[]
  
  // Actions
  setOnlineStatus: (online: boolean) => void
  setSyncing: (syncing: boolean) => void
  addToQueue: (item: Omit<SyncQueueItem, 'id' | 'createdAt' | 'retryCount' | 'status'>) => void
  removeFromQueue: (id: string) => void
  updateQueueItem: (id: string, updates: Partial<SyncQueueItem>) => void
  markAsFailed: (id: string, error: string) => void
  retryFailed: (id: string) => void
  clearCompleted: () => void
  resetQueue: () => void
  setLastSync: (date: Date) => void
}

export const useOfflineStore = create<OfflineState>()(
  persist(
    (set) => ({
      isOnline: navigator.onLine,
      isSyncing: false,
      lastSyncAt: null,
      pendingChanges: 0,
      syncQueue: [],
      failedSyncs: [],

      setOnlineStatus: (isOnline) => set({ isOnline }),

      setSyncing: (isSyncing) => set({ isSyncing }),

      addToQueue: (item) => {
        const newItem: SyncQueueItem = {
          ...item,
          id: Math.random().toString(36).substring(2, 9),
          createdAt: new Date(),
          retryCount: 0,
          status: 'pending'
        }
        
        set((state) => ({
          syncQueue: [...state.syncQueue, newItem],
          pendingChanges: state.pendingChanges + 1
        }))
      },

      removeFromQueue: (id) => {
        set((state) => ({
          syncQueue: state.syncQueue.filter((item) => item.id !== id),
          pendingChanges: Math.max(0, state.pendingChanges - 1)
        }))
      },

      updateQueueItem: (id, updates) => {
        set((state) => ({
          syncQueue: state.syncQueue.map((item) =>
            item.id === id ? { ...item, ...updates } : item
          )
        }))
      },

      markAsFailed: (id, error) => {
        set((state) => {
          const item = state.syncQueue.find((i) => i.id === id)
          if (!item) return state

          const failedItem = { ...item, status: 'failed' as const, error }
          
          return {
            syncQueue: state.syncQueue.filter((i) => i.id !== id),
            failedSyncs: [...state.failedSyncs, failedItem],
            pendingChanges: Math.max(0, state.pendingChanges - 1)
          }
        })
      },

      retryFailed: (id) => {
        set((state) => {
          const item = state.failedSyncs.find((i) => i.id === id)
          if (!item) return state

          const retriedItem = {
            ...item,
            status: 'pending' as const,
            error: undefined,
            retryCount: item.retryCount + 1,
            lastAttemptAt: new Date()
          }
          
          return {
            failedSyncs: state.failedSyncs.filter((i) => i.id !== id),
            syncQueue: [...state.syncQueue, retriedItem],
            pendingChanges: state.pendingChanges + 1
          }
        })
      },

      clearCompleted: () => {
        set((state) => ({
          syncQueue: state.syncQueue.filter((item) => item.status !== 'completed'),
          pendingChanges: state.syncQueue.filter((item) => item.status !== 'completed').length
        }))
      },

      resetQueue: () => set({
        syncQueue: [],
        failedSyncs: [],
        pendingChanges: 0
      }),

      setLastSync: (lastSyncAt) => set({ lastSyncAt })
    }),
    {
      name: 'bsnl-offline-storage',
      partialize: (state) => ({
        syncQueue: state.syncQueue,
        failedSyncs: state.failedSyncs,
        lastSyncAt: state.lastSyncAt,
        pendingChanges: state.pendingChanges
      })
    }
  )
)

// Listen for online/offline events
if (typeof window !== 'undefined') {
  window.addEventListener('online', () => {
    useOfflineStore.getState().setOnlineStatus(true)
  })
  
  window.addEventListener('offline', () => {
    useOfflineStore.getState().setOnlineStatus(false)
  })
}

export default useOfflineStore
